import { Link } from "react-router-dom";
import { toast } from "sonner";
import { API_BASE } from "@/lib/api";
import PublicHeader from "@/components/layout/PublicHeader";
import Footer from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Download, FileText, Box, ImageIcon, Copy, Mail, ArrowRight, Newspaper } from "lucide-react";

const FACTS = [
  ["Cadence", "Every Friday, year-round"],
  ["Schedule", "12 PM lunch + networking \u00b7 1\u20132 PM live podcast \u00b7 2\u20133 PM overtime (3rd Friday at Liberty Lake Portal: 5\u20138 PM)"],
  ["Format", "In-person and free to attend \u2014 all are welcome"],
  ["Location", "Spokane, WA \u2014 four rotating venues"],
  ["Media", "Live podcast with audio, video, transcripts & social-ready clips"],
];

const THEMES = [
  ["1st Friday", "Arts & Entertainment"],
  ["2nd Friday", "Generosity"],
  ["3rd Friday", "Youth & Next Generation"],
  ["4th Friday", "Intentionality"],
];

const BOILERPLATE =
  "Friday Conversations is a weekly, in-person gathering in Spokane, Washington built on a simple " +
  "idea: real conversations lead to deeper connection. Every Friday, members, guests, and neighbors " +
  "come together over lunch for an hour-long live podcast on the week's theme, then stay for overtime " +
  "to keep the conversation going. Four rotating themes give each Friday its own focus, while one " +
  "shared rhythm keeps the community connected all month long.";

function copy(text, label) {
  if (navigator?.clipboard?.writeText) {
    navigator.clipboard.writeText(text).then(() => toast.success(`${label} copied`)).catch(() => {});
  }
}

function Swatch({ name, hex, testid, swatchClass }) {
  return (
    <button
      type="button"
      onClick={() => copy(hex, hex)}
      className="group flex items-center gap-3 rounded-xl border border-border bg-card p-3 text-left transition-colors hover:bg-secondary"
      data-testid={testid}
    >
      <span className={`h-10 w-10 shrink-0 rounded-lg border border-border ${swatchClass}`} />
      <span className="min-w-0">
        <span className="block text-sm font-medium text-foreground">{name}</span>
        <span className="block text-xs text-muted-foreground tabular-nums">{hex}</span>
      </span>
      <Copy className="ml-auto h-4 w-4 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100" />
    </button>
  );
}

export default function Press() {
  const pdfUrl = `${API_BASE}/press/fact-sheet.pdf`;
  return (
    <div className="min-h-screen bg-background">
      <PublicHeader />

      <div className="fc-hero-bg fc-paper-noise">
        <div className="mx-auto w-full max-w-6xl px-4 pb-10 pt-14 sm:px-6 lg:px-8">
          <span className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1 text-xs font-medium text-muted-foreground">
            <Newspaper className="h-3.5 w-3.5 text-primary" /> Press &amp; Media Kit
          </span>
          <h1 className="mt-4 max-w-3xl font-serif text-3xl font-semibold tracking-[-0.02em] text-foreground sm:text-4xl lg:text-5xl">
            Everything you need to tell our story
          </h1>
          <p className="mt-4 max-w-[62ch] text-base leading-7 text-muted-foreground sm:text-lg">
            Brand assets, boilerplate, and key facts about Friday Conversations. For interviews,
            features, or anything else, reach out anytime.
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-[hsl(var(--primary-hover))]" data-testid="press-download-pdf">
              <a href={pdfUrl} target="_blank" rel="noopener noreferrer"><FileText className="mr-1.5 h-4 w-4" />Download fact sheet (PDF)</a>
            </Button>
            <Button asChild size="lg" variant="secondary" data-testid="press-contact-email">
              <a href="mailto:Chris@wannaware.com"><Mail className="mr-1.5 h-4 w-4" />Chris@wannaware.com</a>
            </Button>
          </div>
        </div>
      </div>

      <div className="mx-auto grid w-full max-w-6xl gap-10 px-4 py-12 sm:px-6 lg:grid-cols-3 lg:px-8">
        {/* Main column */}
        <div className="lg:col-span-2 space-y-10">
          <section>
            <h2 className="font-serif text-2xl font-semibold text-foreground">About</h2>
            <p className="mt-3 max-w-[68ch] leading-7 text-muted-foreground">{BOILERPLATE}</p>
            <Button variant="ghost" size="sm" className="mt-2 text-muted-foreground hover:text-foreground" onClick={() => copy(BOILERPLATE, "Boilerplate")} data-testid="press-copy-boilerplate">
              <Copy className="mr-1.5 h-3.5 w-3.5" />Copy boilerplate
            </Button>
          </section>

          <section>
            <h2 className="font-serif text-2xl font-semibold text-foreground">At a glance</h2>
            <div className="mt-4 overflow-hidden rounded-2xl border border-border">
              {FACTS.map(([k, v], i) => (
                <div key={k} className={`grid grid-cols-3 gap-3 p-4 text-sm ${i % 2 ? "bg-card" : "bg-secondary/40"}`}>
                  <span className="font-medium text-foreground">{k}</span>
                  <span className="col-span-2 text-muted-foreground">{v}</span>
                </div>
              ))}
            </div>
          </section>

          <section>
            <h2 className="font-serif text-2xl font-semibold text-foreground">The Friday Rhythm</h2>
            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              {THEMES.map(([ord, theme]) => (
                <Card key={ord} className="border-border bg-card">
                  <CardContent className="p-4">
                    <p className="text-xs font-semibold uppercase tracking-wider text-primary">{ord}</p>
                    <p className="mt-1 font-medium text-foreground">{theme}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>
        </div>

        {/* Assets column */}
        <div className="space-y-6">
          <Card className="border-border bg-card">
            <CardContent className="p-5">
              <h3 className="font-serif text-lg font-semibold text-foreground">Logo</h3>
              <div className="mt-3 flex items-center justify-center rounded-xl border border-border bg-secondary/40 p-6">
                <img src="/logo-mark.png" alt="Friday Conversations logo" className="h-28 w-28 object-contain" />
              </div>
              <div className="mt-4 space-y-2">
                <Button asChild variant="secondary" className="w-full justify-start" data-testid="press-download-logo">
                  <a href="/logo-mark.png" download><ImageIcon className="mr-2 h-4 w-4" />Logo mark (PNG)<Download className="ml-auto h-4 w-4" /></a>
                </Button>
                <Button asChild variant="secondary" className="w-full justify-start" data-testid="press-download-glb">
                  <a href="/models/logo.glb" download><Box className="mr-2 h-4 w-4" />3D logo (.glb)<Download className="ml-auto h-4 w-4" /></a>
                </Button>
                <Button asChild variant="secondary" className="w-full justify-start" data-testid="press-download-pdf-2">
                  <a href={pdfUrl} target="_blank" rel="noopener noreferrer"><FileText className="mr-2 h-4 w-4" />Fact sheet (PDF)<Download className="ml-auto h-4 w-4" /></a>
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card className="border-border bg-card">
            <CardContent className="p-5">
              <h3 className="font-serif text-lg font-semibold text-foreground">Colors</h3>
              <div className="mt-3 space-y-2">
                <Swatch name="Deep Blue" hex="#082D50" testid="press-color-blue" swatchClass="bg-[#082D50]" />
                <Swatch name="Copper" hex="#C9772E" testid="press-color-copper" swatchClass="bg-[#C9772E]" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-border bg-card">
            <CardContent className="p-5">
              <h3 className="font-serif text-lg font-semibold text-foreground">Typography</h3>
              <p className="mt-2 text-sm text-muted-foreground">Headings &amp; body</p>
              <p className="mt-1 text-2xl font-semibold text-foreground" style={{ fontFamily: "Poppins, sans-serif" }}>Poppins</p>
              <p className="text-sm text-muted-foreground" style={{ fontFamily: "Poppins, sans-serif" }}>AaBbCc 0123456789</p>
            </CardContent>
          </Card>

          <Card className="border-border bg-card">
            <CardContent className="p-5">
              <h3 className="font-serif text-lg font-semibold text-foreground">Partner with us</h3>
              <p className="mt-1 text-sm text-muted-foreground">Interested in sponsoring or guesting?</p>
              <Button asChild className="mt-3 w-full bg-primary text-primary-foreground hover:bg-[hsl(var(--primary-hover))]">
                <Link to="/partner">Partner inquiry <ArrowRight className="ml-1 h-4 w-4" /></Link>
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>

      <Footer />
    </div>
  );
}
